import type { ProductOption, UpdateProductOptionRequest } from "@przezmorze/api-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { createProductOption, deleteProductOption, reorderProductOptions, updateProductOption } from "./productsApi";

export const useProductOptions = (productId: string) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const createOptionMutation = useMutation<{ id: string; name: string }, unknown, { name: string }>({
    mutationFn: (r) => createProductOption(productId, r),
    onSuccess: async (data) => {
      enqueueSnackbar(`Opcja ${data.name} została dodana`, { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["products", productId] });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać opcji produktu. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateOptionMutation = useMutation<ProductOption, unknown, { id: string; request: UpdateProductOptionRequest }>(
    {
      mutationFn: ({ id, request }) => updateProductOption(productId, id, request),
      onSuccess: async (data) => {
        enqueueSnackbar(`Opcja ${data.name} została zaktualizowana`, { variant: "success" });
        await queryClient.invalidateQueries({ queryKey: ["products", productId] });
        await queryClient.invalidateQueries({ queryKey: ["products"] });
      },
      onError: (error) => {
        enqueueSnackbar(`Nie udało się zaktualizować opcji produktu. Powód: ${(error as Error).message}`, {
          variant: "error",
        });
      },
    },
  );

  const deleteOptionMutation = useMutation<void, unknown, { id: string }>({
    mutationFn: ({ id }) => deleteProductOption(productId, id),
    onSuccess: async () => {
      enqueueSnackbar("Opcja została usunięta", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["products", productId] });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć opcji. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const reorderOptionsMutation = useMutation<void, unknown, { optionIds: string[] }>({
    mutationFn: ({ optionIds }) => reorderProductOptions(productId, optionIds),
    onSuccess: async () => {
      enqueueSnackbar("Kolejność opcji została zaktualizowana", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["products", productId] });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować kolejności opcji. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    createOption: (name: string) => createOptionMutation.mutateAsync({ name }),
    updateOption: (optionId: string, request: UpdateProductOptionRequest) =>
      updateOptionMutation.mutateAsync({ id: optionId, request }),
    deleteOption: (optionId: string) => deleteOptionMutation.mutateAsync({ id: optionId }),
    reorderOptions: (optionIds: string[]) => reorderOptionsMutation.mutateAsync({ optionIds }),
  };
};
